import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
    ArrowLeft,
    ChatTeardropDots,
    WhatsappLogo,
    EnvelopeSimple,
    Phone,
    DownloadSimple,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = ["new", "contacted", "converted", "dropped"];
const STATUS_COLORS = {
    new: "bg-blue-100 text-blue-700 border-blue-300",
    contacted: "bg-amber-100 text-amber-700 border-amber-300",
    converted: "bg-emerald-100 text-emerald-700 border-emerald-300",
    dropped: "bg-zinc-100 text-zinc-600 border-zinc-300",
};

function formatDate(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString();
    } catch (_e) {
        return s;
    }
}

export default function AdminLeads() {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("");
    const [sourceFilter, setSourceFilter] = useState("");
    const [editing, setEditing] = useState(null);
    const [note, setNote] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (sourceFilter) params.source = sourceFilter;
            const { data } = await api.get("/leads", { params });
            setLeads(data);
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, sourceFilter]);

    const updateLead = async (id, patch) => {
        try {
            await api.put(`/leads/${id}`, patch);
            toast.success("Updated");
            setEditing(null);
            setNote("");
            load();
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        }
    };

    const sources = useMemo(() => {
        const set = new Set(leads.map((l) => l.source).filter(Boolean));
        return Array.from(set);
    }, [leads]);

    const counts = useMemo(() => {
        const acc = { total: leads.length };
        STATUS_OPTIONS.forEach((s) => (acc[s] = 0));
        leads.forEach((l) => {
            if (acc[l.status] !== undefined) acc[l.status]++;
        });
        return acc;
    }, [leads]);

    const exportCsv = () => {
        const headers = [
            "created_at",
            "name",
            "organization",
            "mobile",
            "email",
            "source",
            "status",
            "message",
            "page_url",
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "referrer",
            "admin_note",
        ];
        const escape = (v) => {
            if (v === null || v === undefined) return "";
            const s = String(v).replace(/"/g, '""');
            return /[",\n]/.test(s) ? `"${s}"` : s;
        };
        const rows = leads.map((l) =>
            headers.map((h) => escape(l[h])).join(","),
        );
        const csv = [headers.join(","), ...rows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `idcardgen-leads-${new Date()
            .toISOString()
            .slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-10"
            data-testid="admin-leads-page"
        >
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div>
                    <Link
                        to="/admin"
                        className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
                    >
                        <ArrowLeft size={12} /> Admin
                    </Link>
                    <h1 className="font-display font-black text-3xl tracking-tighter mt-1 flex items-center gap-3">
                        <ChatTeardropDots
                            size={28}
                            weight="duotone"
                            className="text-blue-600"
                        />
                        Leads
                    </h1>
                    <p className="text-sm text-zinc-500 mt-1">
                        Demo requests, WhatsApp clicks and other inbound
                        contacts.
                    </p>
                </div>
                <button
                    onClick={exportCsv}
                    className="btn-secondary flex items-center gap-2"
                    data-testid="export-leads-csv"
                >
                    <DownloadSimple size={14} weight="bold" /> Export CSV
                </button>
            </div>

            {/* Stat strip */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                {[
                    { k: "total", label: "Total" },
                    ...STATUS_OPTIONS.map((s) => ({ k: s, label: s })),
                ].map((c) => (
                    <div
                        key={c.k}
                        className="border border-zinc-200 bg-white p-3 text-center"
                    >
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                            {c.label}
                        </div>
                        <div className="font-display font-black text-2xl tracking-tighter">
                            {counts[c.k] ?? 0}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="input-swiss !w-auto"
                    data-testid="filter-status"
                >
                    <option value="">All statuses</option>
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <select
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    className="input-swiss !w-auto"
                    data-testid="filter-source"
                >
                    <option value="">All sources</option>
                    {sources.map((s) => (
                        <option key={s} value={s}>
                            {s}
                        </option>
                    ))}
                </select>
                <button onClick={load} className="btn-ghost text-sm">
                    Refresh
                </button>
            </div>

            <div className="border border-zinc-200 bg-white overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs uppercase tracking-widest text-zinc-500">
                        <tr>
                            <th className="text-left p-3">When</th>
                            <th className="text-left p-3">Name</th>
                            <th className="text-left p-3">Organization</th>
                            <th className="text-left p-3">Contact</th>
                            <th className="text-left p-3">Source</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Message</th>
                            <th className="text-left p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="p-6 text-center text-zinc-500"
                                >
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && leads.length === 0 && (
                            <tr>
                                <td
                                    colSpan={8}
                                    className="p-6 text-center text-zinc-500"
                                >
                                    No leads yet.
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            leads.map((l) => (
                                <tr
                                    key={l.id}
                                    className="border-t border-zinc-100 align-top hover:bg-zinc-50/50"
                                >
                                    <td className="p-3 text-xs text-zinc-500 font-mono whitespace-nowrap">
                                        {formatDate(l.created_at)}
                                    </td>
                                    <td className="p-3 font-semibold">
                                        {l.name || "—"}
                                    </td>
                                    <td className="p-3 text-zinc-700">
                                        {l.organization || "—"}
                                    </td>
                                    <td className="p-3 space-y-1">
                                        {l.mobile && (
                                            <div className="flex items-center gap-2">
                                                <a
                                                    href={`https://wa.me/91${l.mobile}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-emerald-600 hover:underline inline-flex items-center gap-1 text-xs"
                                                >
                                                    <WhatsappLogo
                                                        size={12}
                                                        weight="fill"
                                                    />
                                                    {l.mobile}
                                                </a>
                                                <a
                                                    href={`tel:${l.mobile}`}
                                                    className="text-zinc-500 hover:text-zinc-900"
                                                    title="Call"
                                                >
                                                    <Phone size={12} />
                                                </a>
                                            </div>
                                        )}
                                        {l.email && (
                                            <a
                                                href={`mailto:${l.email}`}
                                                className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"
                                            >
                                                <EnvelopeSimple size={12} />
                                                {l.email}
                                            </a>
                                        )}
                                        {!l.mobile && !l.email && (
                                            <span className="text-xs text-zinc-400">
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <span className="inline-block bg-zinc-100 border border-zinc-200 text-[10px] uppercase tracking-widest font-mono px-2 py-0.5">
                                            {l.source}
                                        </span>
                                        {(l.utm_source || l.utm_campaign) && (
                                            <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                                                {[l.utm_source, l.utm_campaign]
                                                    .filter(Boolean)
                                                    .join(" / ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <select
                                            value={l.status}
                                            onChange={(e) =>
                                                updateLead(l.id, {
                                                    status: e.target.value,
                                                })
                                            }
                                            className={`text-xs border px-2 py-1 font-semibold uppercase tracking-widest ${
                                                STATUS_COLORS[l.status] ||
                                                STATUS_COLORS.new
                                            }`}
                                        >
                                            {STATUS_OPTIONS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="p-3 max-w-xs">
                                        {l.message ? (
                                            <div className="text-xs text-zinc-700 whitespace-pre-wrap">
                                                {l.message}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-zinc-400">
                                                —
                                            </span>
                                        )}
                                        {l.admin_note && (
                                            <div className="mt-1 text-[10px] text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-2 py-1">
                                                {l.admin_note}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-3">
                                        <button
                                            onClick={() => {
                                                setEditing(l.id);
                                                setNote(l.admin_note || "");
                                            }}
                                            className="text-xs text-blue-600 hover:underline"
                                        >
                                            Note
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4"
                    onClick={() => setEditing(null)}
                >
                    <div
                        className="bg-white border border-zinc-200 w-full max-w-md p-5 space-y-3"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="font-display font-bold text-lg">
                            Internal note
                        </div>
                        <textarea
                            rows={5}
                            className="input-swiss"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Spoke to principal, send PVC sample…"
                        />
                        <div className="flex justify-end gap-2">
                            <button
                                className="btn-ghost"
                                onClick={() => setEditing(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                onClick={() =>
                                    updateLead(editing, { admin_note: note })
                                }
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
